/**
 * Shopping Cart Page
 * Client-facing cart with items, promo codes, and checkout
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import type { Cart, CartItem } from '@phoenixbill/shared/types/cart';

export default function CartPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    try {
      setLoading(true);
      const response = await api.get('/client/cart');
      setCart(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    if (quantity < 1) return;

    try {
      const response = await api.put(`/client/cart/${itemId}`, { quantity });
      setCart(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update item');
    }
  };

  const updateBillingCycle = async (itemId: string, billingCycle: string) => {
    try {
      const response = await api.put(`/client/cart/${itemId}`, { billingCycle });
      setCart(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update billing cycle');
    }
  };

  const removeItem = async (itemId: string) => {
    if (!confirm('Remove this item from cart?')) return;

    try {
      const response = await api.delete(`/client/cart/${itemId}`);
      setCart(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to remove item');
    }
  };

  const clearCart = async () => {
    if (!confirm('Clear entire cart?')) return;

    try {
      await api.delete('/client/cart');
      setCart(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to clear cart');
    }
  };

  const applyPromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoCode.trim()) return;

    try {
      setPromoLoading(true);
      setPromoError('');
      const response = await api.post('/client/cart/promo', { promoCode: promoCode.trim() });
      setCart(response.data.data);
      setPromoCode('');
    } catch (err: any) {
      setPromoError(err.response?.data?.error?.message || 'Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = async () => {
    try {
      const response = await api.delete('/client/cart/promo');
      setCart(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to remove promo code');
    }
  };

  const proceedToCheckout = () => {
    navigate('/client/checkout');
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatBillingCycle = (cycle: string) => {
    const labels: Record<string, string> = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannually: 'Semi-Annually',
      annually: 'Annually',
      biennially: 'Biennially',
      triennially: 'Triennially',
      onetime: 'One Time',
    };
    return labels[cycle] || cycle;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8 text-center">
          <svg
            className="mx-auto h-16 w-16 text-slate-600 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h3 className="text-xl font-semibold text-gray-200 mb-2">Your cart is empty</h3>
          <p className="text-slate-400 mb-6">Add some products to get started</p>
          <button
            onClick={() => navigate('/client/products')}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-100 mb-2">Shopping Cart</h1>
        <p className="text-slate-400">{cart.items.length} item(s) in your cart</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map(item => (
            <CartItemCard
              key={item.id}
              item={item}
              onUpdateQuantity={updateQuantity}
              onUpdateBillingCycle={updateBillingCycle}
              onRemove={removeItem}
              formatPrice={formatPrice}
              formatBillingCycle={formatBillingCycle}
            />
          ))}

          <div className="flex justify-between items-center pt-4">
            <button
              onClick={clearCart}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              Clear Cart
            </button>
            <button
              onClick={() => navigate('/client/products')}
              className="text-orange-400 hover:text-orange-300 transition-colors"
            >
              ← Continue Shopping
            </button>
          </div>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 sticky top-4">
            <h2 className="text-xl font-semibold text-gray-100 mb-4">Order Summary</h2>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-slate-300">
                <span>Subtotal</span>
                <span>{formatPrice(cart.subtotal)}</span>
              </div>

              {cart.setupFeeTotal > 0 && (
                <div className="flex justify-between text-slate-300">
                  <span>Setup Fees</span>
                  <span>{formatPrice(cart.setupFeeTotal)}</span>
                </div>
              )}

              {cart.discount > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Discount</span>
                  <span>-{formatPrice(cart.discount)}</span>
                </div>
              )}

              {cart.taxAmount > 0 && (
                <div className="flex justify-between text-slate-300">
                  <span>Tax ({(cart.taxRate * 100).toFixed(1)}%)</span>
                  <span>{formatPrice(cart.taxAmount)}</span>
                </div>
              )}

              <div className="border-t border-slate-700/50 pt-3">
                <div className="flex justify-between text-lg font-semibold text-gray-100">
                  <span>Total</span>
                  <span>{formatPrice(cart.total)}</span>
                </div>
              </div>
            </div>

            {/* Promo Code */}
            <div className="mb-4">
              {cart.promoCode ? (
                <div className="bg-green-900/20 border border-green-800 rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <p className="text-green-400 font-medium">{cart.promoCode.code}</p>
                    <p className="text-xs text-slate-400">
                      {cart.promoCode.discountType === 'percentage'
                        ? `${cart.promoCode.discountValue}% off`
                        : `${formatPrice(cart.promoCode.discountValue)} off`}
                    </p>
                  </div>
                  <button
                    onClick={removePromoCode}
                    className="text-red-400 hover:text-red-300"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <form onSubmit={applyPromoCode} className="space-y-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={e => setPromoCode(e.target.value)}
                    placeholder="Promo code"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  {promoError && <p className="text-xs text-red-400">{promoError}</p>}
                  <button
                    type="submit"
                    disabled={promoLoading || !promoCode.trim()}
                    className="w-full px-4 py-2 bg-slate-700/50 text-gray-200 rounded-lg hover:bg-slate-600/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {promoLoading ? 'Applying...' : 'Apply Code'}
                  </button>
                </form>
              )}
            </div>

            {/* Checkout Button */}
            <button
              onClick={proceedToCheckout}
              className="w-full px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold"
            >
              Proceed to Checkout
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              Secure checkout with SSL encryption
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Cart Item Card Component
interface CartItemCardProps {
  item: CartItem;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onUpdateBillingCycle: (itemId: string, billingCycle: string) => void;
  onRemove: (itemId: string) => void;
  formatPrice: (amount: number, currency?: string) => string;
  formatBillingCycle: (cycle: string) => string;
}

function CartItemCard({
  item,
  onUpdateQuantity,
  onUpdateBillingCycle,
  onRemove,
  formatPrice,
  formatBillingCycle,
}: CartItemCardProps) {
  const billingCycles = ['monthly', 'quarterly', 'semiannually', 'annually', 'biennially', 'triennially', 'onetime'];

  return (
    <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-100 mb-1">{item.productName}</h3>
          {item.domain && (
            <p className="text-sm text-slate-400 mb-2">
              <span className="text-gray-500">Domain:</span> {item.domain}
            </p>
          )}
        </div>
        <button
          onClick={() => onRemove(item.id)}
          className="text-red-400 hover:text-red-300 ml-4"
          title="Remove item"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Quantity */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Quantity</label>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
              disabled={item.quantity <= 1}
              className="px-3 py-1 bg-slate-700/50 text-gray-200 rounded hover:bg-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              -
            </button>
            <span className="px-4 py-1 bg-slate-900 border border-slate-700/50 rounded text-gray-100 min-w-[60px] text-center">
              {item.quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
              className="px-3 py-1 bg-slate-700/50 text-gray-200 rounded hover:bg-slate-600/50"
            >
              +
            </button>
          </div>
        </div>

        {/* Billing Cycle */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Billing Cycle</label>
          <select
            value={item.billingCycle}
            onChange={e => onUpdateBillingCycle(item.id, e.target.value)}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {billingCycles.map(cycle => (
              <option key={cycle} value={cycle}>
                {formatBillingCycle(cycle)}
              </option>
            ))}
          </select>
        </div>

        {/* Price */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Price</label>
          <div className="text-gray-100 font-semibold py-2">
            {formatPrice(item.price)}
            {item.billingCycle !== 'onetime' && <span className="text-sm text-slate-400">/{item.billingCycle}</span>}
          </div>
        </div>
      </div>

      {/* Pricing Breakdown */}
      <div className="border-t border-slate-700/50 pt-4 space-y-2">
        <div className="flex justify-between text-sm text-slate-300">
          <span>Subtotal ({item.quantity}x)</span>
          <span>{formatPrice(item.subtotal)}</span>
        </div>
        {item.setupFee > 0 && (
          <div className="flex justify-between text-sm text-slate-300">
            <span>Setup Fee</span>
            <span>{formatPrice(item.totalSetupFee)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-gray-100 pt-2 border-t border-slate-700/50">
          <span>Item Total</span>
          <span>{formatPrice(item.subtotal + item.totalSetupFee)}</span>
        </div>
      </div>

      {/* Configurable Options */}
      {item.configurableOptions && Object.keys(item.configurableOptions).length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-700/50">
          <p className="text-xs text-gray-500 mb-2">Configuration:</p>
          <div className="space-y-1">
            {Object.entries(item.configurableOptions).map(([key, value]) => (
              <div key={key} className="text-xs text-slate-400">
                <span className="text-gray-500">{key}:</span> {String(value)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
